import { forwardRef, ReactNode, useState } from 'react';

import { Button, ButtonProps } from '@gearbelt/ui/components';

import CheckIcon from '@/components/icons/check';
import GearIcon from '@/components/icons/gear';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface TablePageHeaderProps {
  children?: ReactNode;
}

export function TablePageHeader({ children }: TablePageHeaderProps) {
  return <div className="bg-grey-2 relative z-10 flex min-h-[45px] w-full items-center justify-between">{children}</div>;
}

export const TablePageHeaderActionButton = forwardRef<
  HTMLButtonElement,
  Omit<ButtonProps, 'icon'> & { icon: ReactNode; label: string; trailingIcon?: ReactNode; kind?: 'primary' | 'secondary' }
>(({ icon, label, trailingIcon, variant = 'ghost', kind = 'secondary', ...props }, ref) => {
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            ref={ref}
            className={cn(
              'flex h-full items-center gap-1 rounded-md p-2 font-medium',
              variant === 'ghost' && 'hover:bg-grey-3 focus-visible:ring-0 focus-visible:ring-offset-0',
            )}
            variant={variant}
            // The label span is `display:none` below xxl/3xl, which drops it from the accessible name.
            aria-label={label}
            {...props}>
            {icon}
            <span className={cn('hidden leading-none', kind === 'primary' ? 'xxl:inline' : '3xl:inline')}>{label}</span>
            {trailingIcon}
          </Button>
        </TooltipTrigger>
        <TooltipContent className={cn(kind === 'primary' ? 'xxl:hidden' : '3xl:hidden')}>{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
});
TablePageHeaderActionButton.displayName = 'TablePageHeaderActionButton';

export function TablePageTableGroupDropdown({
  columns,
  value,
  onChange,
}: {
  columns: { label: string; accessor: string }[];
  value: string;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <TablePageHeaderActionButton icon={<GearIcon />} label="Group" />
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {columns.map((column) => (
          <DropdownMenuItem
            key={column.accessor}
            onClick={() => {
              onChange(column.accessor);
              setOpen(false);
            }}>
            <CheckIcon className={cn('mr-1 size-4 shrink-0 opacity-0', value === column.accessor && 'opacity-100')} />
            {column.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
